'use client';

import { formatPercentage } from '@benzinga/utils';
import { formatHoldingCurrency } from '../portfolioFormatters';
import { WatchlistStats } from '../WatchlistPerformance/processors';

const WatchlistSummaryAside = ({ stats }: { stats: WatchlistStats }) => (
  <div className="flex flex-col justify-end gap-3 md:items-end md:text-right">
    <div>
      <div className="text-lg font-bold text-white tabular-nums md:text-2xl">
        {stats.count}
        <span className="ml-1.5 text-base font-semibold text-white/80">symbols tracked</span>
      </div>
      {(stats.positive_count > 0 || stats.negative_count > 0) && (
        <div className="mt-3 w-full max-w-sm md:ml-auto">
          <div className="flex h-2.5 overflow-hidden rounded-full bg-bzblue-800">
            {stats.positive_percentage > 0 && (
              <div className="bg-green-300" style={{ width: `${stats.positive_percentage}%` }} />
            )}
            {stats.negative_percentage > 0 && (
              <div className="bg-red-300" style={{ width: `${stats.negative_percentage}%` }} />
            )}
          </div>
          <div className="mt-1.5 flex justify-between text-xs font-semibold">
            <span className="text-green-300">{stats.positive_count} advancing</span>
            <span className="text-red-300">{stats.negative_count} declining</span>
          </div>
        </div>
      )}
    </div>
  </div>
);

export const DailyOverview = ({ stats }: { stats: WatchlistStats }) => (
  <div
    className={`grid grid-cols-1 gap-6 bg-bzblue-700 p-6 text-white ${
      stats.cost > 0 || stats.count > 0 ? 'md:grid-cols-2' : ''
    }`}
  >
    <div className="flex flex-col gap-2">
      <div className="max-w-xl">
        <div className="text-xl font-bold text-white">Your Daily Overview</div>
        <div className="text-4xl font-bold text-balance text-white">{stats.greeting}</div>
        <p
          className={
            stats.top_moving_positive.length > 0 || stats.top_moving_negative.length > 0
              ? 'mt-2 block text-pretty text-white/90'
              : 'hidden'
          }
        >
          <span className={stats.top_moving_positive.length > 0 ? 'block' : 'hidden'}>
            Looks like
            <strong className="mx-1 font-bold text-white">{stats.top_moving_positive_message}</strong>
            {stats.top_moving_positive.length === 1
              ? 'is making a significant move up.'
              : 'are moving up significantly.'}
          </span>
          <span className={stats.top_moving_negative.length > 0 ? 'block' : 'hidden'}>
            Looks like
            <strong className="mx-1 font-bold text-white">{stats.top_moving_negative_message}</strong>
            {stats.top_moving_negative.length === 1
              ? 'is making a significant move down.'
              : 'are moving down significantly.'}
          </span>
        </p>
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
          <div className="rounded-lg bg-bzblue-800 px-4 py-1 font-bold text-white">
            <span className="mr-1 tabular-nums">{stats.percentage_avg?.toFixed(2)}%</span>
            <span>{stats.percentage_weighting === 'portfolio' ? 'Portfolio today' : 'Watchlist avg'}</span>
          </div>
          <div className="rounded-lg bg-green-300 px-4 py-1 font-bold text-bzblue-1000">
            <span className="mr-1 tabular-nums">{stats.positive_count}</span>
            <span>Win{stats.positive_count === 1 ? '' : 's'}</span>
          </div>
          <div className="rounded-lg bg-red-300 px-4 py-1 font-bold text-bzblue-1000">
            <span className="mr-1 tabular-nums">{stats.negative_count}</span>
            <span>Loss{stats.negative_count === 1 ? '' : 'es'}</span>
          </div>
        </div>
      </div>
    </div>
    {stats.cost > 0 ? (
      <div className="flex flex-wrap gap-4">
        <div className="flex-1">
          <div className="text-lg font-bold">Total Balance</div>
          <div className="text-4xl font-bold tabular-nums">{formatHoldingCurrency(stats.marketValue)}</div>
        </div>
        <div className="flex-1">
          <div className="text-lg font-bold">Total Gains</div>
          <div className="text-4xl font-bold tabular-nums">{formatHoldingCurrency(stats.gain)}</div>
          <div>Pct. Gain: {formatPercentage(stats.gain_percentage)}%</div>
        </div>
      </div>
    ) : (
      stats.count > 0 && <WatchlistSummaryAside stats={stats} />
    )}
  </div>
);
